import React, {ReactElement, useCallback, useMemo, useState} from 'react';

import {useAppDispatch} from '../../store';
import {Actions} from '../gameSlice';
import {useEnabledTools, useGameStatus, useMyTools} from '../hooks';
import {useGameSettings} from '../hooks/useGameSettings';
import {computeCost} from '../mechanics';
import {Tool} from '../types';
import ArrowButton from './buttons/ArrowButton';
import {ToolsButton} from './buttons/PillButtons';
import ModalButton from './ModalButton';
import ToolCard from './ToolCard';

const TOOLBOX_VIEW_SIZE = 3;

const Toolbox = (): ReactElement | null => {
  const gameStatus = useGameStatus();
  const settings = useGameSettings();
  const dispatch = useAppDispatch();
  const enabledTools = useEnabledTools();
  const myTools = useMyTools();

  const [page, setPage] = useState(0);
  const firstDisplayedItem = useMemo(() => TOOLBOX_VIEW_SIZE * page, [page]);
  const lastDisplayedItem = useMemo(() => firstDisplayedItem + TOOLBOX_VIEW_SIZE - 1, [firstDisplayedItem]);
  const isLastPage = useMemo(
    () => lastDisplayedItem + 1 >= enabledTools.length,
    [enabledTools.length, lastDisplayedItem]
  );

  const buyTool = useCallback(
    (tool: Tool) => {
      try {
        dispatch(Actions.buyTool(tool));
      } catch (err) {
        alert(err);
      }
    },
    [dispatch]
  );

  if (enabledTools.length === 0) return null;
  if (!settings.showToolsMenu) return null;
  return (
    <ModalButton
      title="Tools"
      button={<ToolsButton />}
      left={page !== 0 && <ArrowButton direction="left" onClick={() => setPage(page - 1)} />}
      right={!isLastPage && <ArrowButton direction="right" onClick={() => setPage(page + 1)} />}
      afterClose={() => setPage(0)}
    >
      <div className="grid grid-cols-3 gap-12 mt-md mb-xl w-full h-[45rem] justify-evenly">
        {enabledTools
          .filter((_, i) => i >= firstDisplayedItem && i <= lastDisplayedItem)
          .map(tool => {
            const cost = tool.price ?? computeCost(gameStatus, tool.pricePerHa) ?? 0;
            return (
              <div key={`tool-${tool.id}`}>
                <ToolCard
                  className="h-full"
                  tool={tool}
                  onClick={() => buyTool(tool)}
                  purchased={!!myTools.find(t => t.id === tool.id)}
                  cost={cost}
                  canPay={cost <= gameStatus.money}
                />
              </div>
            );
          })}
      </div>
    </ModalButton>
  );
};

export default Toolbox;
